import { useLocale } from '../i18n/LocaleContext';
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const Testimonials = () => {
  const { t, locale } = useLocale();
  const videos = [
    {
      src: process.env.PUBLIC_URL + "/testimonials/dan_test.mp4",
      author: "Dr. Montagliani",
      quote: t('testimonials.family')
    },
    {
      src: process.env.PUBLIC_URL + "/testimonials/gage_test.mp4",
      author: "Dr. Lidder",
      quote: t('testimonials.quick')
    }
  ];

  const testimonials = [
    { quote: t('testimonials.best'), author: "Dr. Peluso" },
    { quote: t('testimonials.love'), author: "Dr. Aya" },
    { quote: t('testimonials.value'), author: "MRPharmS. Hamby" }
  ];

  return (
    <Section>
      <Container>
        <Header
          as={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Kicker>{t('testimonials.title')}</Kicker>
          {/* <Title>What clinicians say</Title> */}
          <SubTitle>
            {t('testimonials.description')}
          </SubTitle>
        </Header>

        <Layout>
          <ColumnCenter>
            <ColumnTitle>{t('testimonials.video')}</ColumnTitle>
            <AutoReel videos={videos} />
          </ColumnCenter>

          <ColumnCenter>
            <ColumnTitle>{t('testimonials.written')}</ColumnTitle>{locale !== 'en' && <p>{t('testimonials.translated')}</p>}
            <TextGrid>
              {testimonials.map((t, i) => (
                <Card
                  key={i}
                  as={motion.article}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <Quote>“{t.quote}”</Quote>
                  <CardFooter>
                    <AvatarFallback>{initials(t.author)}</AvatarFallback>
                    <div><Author>{t.author}</Author></div>
                  </CardFooter>
                </Card>
              ))}
            </TextGrid>
          </ColumnCenter>
        </Layout>
      </Container>
    </Section>
  );
};

/** AutoReel — plays one video at a time, auto-advances only when video ends */
const AutoReel = ({ videos }) => {
  const { t } = useLocale();
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted; // apply global mute
    v.play().catch(() => {});
    const onEnded = () => setIndex((index + 1) % videos.length);
    v.addEventListener("ended", onEnded);
    return () => v.removeEventListener("ended", onEnded);
  }, [index, muted, videos.length]);

  const toggleMute = () => {
    const v = videoRef.current;
    const nextMuted = !muted;
    setMuted(nextMuted);
    if (v) v.muted = nextMuted;
  };

  return (
    <ReelArea>
      <Frame onClick={toggleMute} role="button" aria-label={muted ? t("video.unmute") : t("video.mute")}>
        <Video
          key={videos[index].src}
          ref={videoRef}
          src={videos[index].src}
          playsInline
          loop={false}
          preload="metadata"
          muted={muted}
        />
        <Badge>{t('testimonials.verified')}</Badge>
        <Caption>
          {videos[index].quote && <CaptionQuote>“{videos[index].quote}”</CaptionQuote>}
          <CaptionMeta>{videos[index].author}</CaptionMeta>
        </Caption>
        <MuteGlyph aria-hidden="true">{muted ? "🔇" : "🔊"}</MuteGlyph>
      </Frame>

      <Pips>
        {videos.map((_, i) => (
          <Pip aria-label={`${t("testimonials.video")} ${i + 1}`} key={i} data-active={i === index} onClick={() => setIndex(i)} />
        ))}
      </Pips>
    </ReelArea>
  );
};

const initials = (name = "?") =>
  name.split(/\s+/).map((n) => n[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();

/* ==================== styled ==================== */
const Section = styled.section`
  padding: 90px 0 110px;
  background: var(--white);
`;
const Container = styled.div`
  max-width: 1200px; margin: 0 auto; padding: 0 20px;
`;
const Header = styled.header`
  text-align: center; margin-bottom: 32px;
`;
const Kicker = styled.div`

  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 15px;
  color: var(--primary);
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;
const SubTitle = styled.p`
  color: var(--secondary); max-width: 760px; margin: 0 auto;
`;

const Layout = styled.div`
  display: grid; gap: 28px;
  @media (min-width: 992px) { grid-template-columns: 1fr 1fr; gap: 36px; }
`;
const Column = styled.div``;
const ColumnCenter = styled(Column)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
const ColumnTitle = styled.h3`
  font-size: 1.1rem; font-weight: 700; color: var(--dark-gray); margin: 0 0 12px;
`;

const ReelArea = styled.div`
  display: grid; place-items: center;
`;
const Frame = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;
  aspect-ratio: 9 / 16;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
  box-shadow: var(--shadow);
  cursor: pointer;
`;
const Video = styled.video`
  width: 100%; height: 100%; object-fit: cover;
`;
const Badge = styled.span`
  position: absolute; top: 10px; inset-inline-start: 10px;
  padding: 4px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 700;
  color: #1f2937; background: rgba(255,255,255,0.9);
`;
const Caption = styled.div`
  position: absolute; inset-inline-start: 0; inset-inline-end: 0; bottom: 0;
  padding: 12px; color: white;
  background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.75) 100%);
`;
const CaptionQuote = styled.p`
  margin: 0 0 4px; font-size: 0.95rem; line-height: 1.35;
`;
const CaptionMeta = styled.div`
  font-size: 0.85rem; opacity: 0.95;
`;
const MuteGlyph = styled.div`
  position: absolute; inset-inline-end: 8px; top: 8px; font-size: 16px;
  background: rgba(255,255,255,0.92); border-radius: 999px; width: 28px; height: 28px;
  display: grid; place-items: center;
`;

const Pips = styled.div`
  display: flex; gap: 6px; justify-content: center; margin-top: 10px;
`;
const Pip = styled.button`
  width: 8px; height: 8px; border-radius: 50%; border: 0; cursor: pointer;
  background: rgba(0,0,0,0.15);
  &[data-active="true"] { background: var(--primary); }
`;

const TextGrid = styled.div`
  display: grid; gap: 14px; grid-template-columns: 1fr;
  @media (min-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`;
const Card = styled.div`
  background: white; border: 1px solid rgba(0,0,0,0.06); border-radius: 16px;
  padding: 18px; box-shadow: 0 4px 18px rgba(0,0,0,0.06);
`;
const Quote = styled.p`
  color: var(--dark-gray); font-size: 1.05rem; line-height: 1.5;
`;
const CardFooter = styled.div`
  display: flex; align-items: center; margin-top: 12px; gap: 10px;
`;
const AvatarFallback = styled.div`
  width: 40px; height: 40px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: #eef2ff; color: #4f46e5; font-weight: 800; font-size: 0.8rem;
`;
const Author = styled.div`
  font-weight: 700; color: var(--dark-gray);
`;

export default Testimonials;
